import './EventBubble.styles.scss';

import React from 'react';
import { EventType } from '../../../../Chat.enum.ts';
import { tChatEvent } from '../../../../Chat.types.ts';
import {
  isInteractiveOutbound,
  isInteractiveReply,
} from '../../../MessageBar/components/InteractiveAction/InteractiveAction.functions.ts';
import AgendaBubble from '../../../MessageBar/components/AgendaAction/components/AgendaBubble.component.tsx';
import FollowUpBubble from './components/FollowUpBubble/FollowUpBubble.component.tsx';
import InteractiveReplyBubble from './components/InteractiveReplyBubble/InteractiveReplyBubble.component.tsx';
import LeadsalesAIBubble from './components/LeadsalesAIBubble/LeadsalesAIBubble.component.tsx';
import MessageBubbleV2 from './components/MessageBubble/MessageBubbleV2.component.tsx';
import NoteBubble from './components/NoteBubble/NoteBubble.component.tsx';
import OutboundInteractiveBubble from './components/OutboundInteractiveBubble/OutboundInteractiveBubble.component.tsx';
import SystemBubble from './components/SystemBubble/SystemBubble.component.tsx';
import TemplateBubbleComponent from './components/TemplateBubble/TemplateBubble.component.tsx';
import { scheduledCardAnchorProps } from '../../../UpcomingSendsBar/UpcomingSendsBar.functions.ts';

function EventBubble({
  event,
  innerRef,
  registerNode,
  scheduledOrigin,
  groupStart = true,
}: {
  event: tChatEvent;
  innerRef: React.RefObject<HTMLDivElement>;
  /**
   * Publishes this bubble's root node under its event id so the thread can be
   * jumped to one specific message (SRE-3328). Optional: every other caller
   * renders bubbles without a node map.
   */
  registerNode?: (eventId: string, node: HTMLElement | null) => void;
  /** INT-1505: the scheduled card this message was sent from, if any. */
  scheduledOrigin?: tChatEvent;
  /** First bubble of a run from one sender (MessageContainer's layout). */
  groupStart?: boolean;
}) {
  // Both consumers want the same node and React takes one ref per element, so
  // they are merged here. Assigning `innerRef.current` by hand reproduces
  // exactly what React did with the object ref before: every mounting bubble
  // writes it in commit order, so it still ends up holding the last one
  // mounted, which is what the pagination scroll-restore reads.
  //
  // Memoized because EventBubble is React.memo'd: an identity that changed on
  // every parent render would detach and reattach every ref in the thread.
  const setNode = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (innerRef)
        (innerRef as React.MutableRefObject<HTMLDivElement | null>).current =
          node;
      registerNode?.(event.id, node);
    },
    [innerRef, registerNode, event.id],
  );

  // Inbound interactive taps render as a dedicated bubble before falling
  // through to the generic MESSAGE handler. Event-shape contract: inbound
  // taps arrive as EventType.MESSAGE with content.interactive.type =
  // 'button_reply' | 'list_reply' (WBS PR #622 / messaging PR #820) or
  // 'flow_reply' (WBS PR #829 / messaging PR #894, INT-1138/BUG-721).
  if (
    event.type === EventType.MESSAGE &&
    isInteractiveReply(event.content?.interactive)
  ) {
    const interactive = event.content!.interactive!;
    return (
      <div ref={setNode}>
        {(interactive.type === 'button_reply' ||
          interactive.type === 'list_reply' ||
          interactive.type === 'flow_reply') && (
          <InteractiveReplyBubble event={event} interactive={interactive} />
        )}
      </div>
    );
  }

  // Outbound interactive messages (button / cta_url / list) have the
  // action buttons nested under content.interactive — the generic text
  // bubble only surfaces content.text, hiding the buttons entirely. Route
  // to a dedicated bubble that lays them out WhatsApp-Web-style.
  if (
    event.type === EventType.MESSAGE &&
    isInteractiveOutbound(event.content?.interactive)
  ) {
    return (
      <div ref={setNode}>
        <OutboundInteractiveBubble
          event={event}
          interactive={event.content!.interactive!}
        />
      </div>
    );
  }

  switch (event.type) {
    // BUG-1063: both deleted types render here. Until INBOUND_DELETED was
    // named, a message the lead deleted hit the null default below and
    // vanished from the thread instead of leaving its tombstone.
    case EventType.MESSAGE:
    case EventType.OUTBOUND_DELETED:
    case EventType.INBOUND_DELETED:
      return (
        <MessageBubbleV2
          event={event}
          innerRef={setNode}
          scheduledOrigin={scheduledOrigin}
          groupStart={groupStart}
        />
      );
    case EventType.FOLLOW_UP:
      return (
        <div
          className="flex justify-end"
          ref={setNode}
          // INT-1500 U12: where "Próximos envíos" → "Ver" scrolls to.
          {...scheduledCardAnchorProps(event.id)}
        >
          <FollowUpBubble {...event} />
        </div>
      );
    case EventType.CALENDAR_EVENT:
      return (
        <div className="flex justify-end" ref={setNode}>
          <AgendaBubble {...event} />
        </div>
      );
    case EventType.NOTE:
      return (
        <div className="flex justify-end" ref={setNode}>
          <NoteBubble {...event} />
        </div>
      );
    case EventType.LEADSALES_AI:
      return (
        <div className="flex justify-end" ref={setNode}>
          <LeadsalesAIBubble aiEvent={event} />
        </div>
      );
    case EventType.TEMPLATE:
      return (
        <div className="flex justify-end" ref={setNode}>
          <TemplateBubbleComponent {...event} scheduledOrigin={scheduledOrigin} />
        </div>
      );
    case EventType.SYSTEM:
      return (
        <div ref={setNode}>
          <SystemBubble event={event} />
        </div>
      );
    default:
      return null;
  }
}

export default React.memo(EventBubble);
